import React, { useState, useContext, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Dialog } from '@headlessui/react'
import { processAlfanoFile, validateAlfanoData } from '../../utils/alfanoUtils'
import { FirebaseContext } from './Firebase'
import SubscriptionWall from '../subscription/SubscriptionWall'
import { requireRacingLineEntitlement } from '../../utils/racingLineEntitlement'
import { trackEvent } from '../../utils/analytics'
import { persistAlfanoTelemetry } from '../../utils/telemetryPersistence'

const AlfanoImport = ({ onImport, renderTrigger, triggerOpen }) => {
    const [isProcessing, setIsProcessing] = useState(false)
    const [isOpen, setIsOpen] = useState(false)
    const [uploadModalOpen, setUploadModalOpen] = useState(false)
    const [fileInfo, setFileInfo] = useState(null)
    const [lapsInfo, setLapsInfo] = useState([])
    const [selectedLap, setSelectedLap] = useState(null)
    const [telemetryData, setTelemetryData] = useState(null)
    const [persistedTelemetry, setPersistedTelemetry] = useState(null)
    const [processingStatus, setProcessingStatus] = useState('')
    const [processingProgress, setProcessingProgress] = useState(0)
    const [error, setError] = useState(null)
    const [isDragging, setIsDragging] = useState(false)
    const auth = useContext(FirebaseContext)
    const { requireAuth } = auth
    const [showSubscriptionWall, setShowSubscriptionWall] = useState(false)
    const [subscriptionContext, setSubscriptionContext] = useState({ title: null, message: null })
    const selectedLapRef = useRef(null)
    const fileInputRef = useRef(null)

    // Watch for triggerOpen changes and open modal when it becomes true
    useEffect(() => {
        if (triggerOpen) {
            requireAuth(() => {
                setUploadModalOpen(true)
                setError(null)
                setProcessingStatus('')
                setProcessingProgress(0)
            })
        }
    }, [triggerOpen, requireAuth])

    useEffect(() => {
        if (selectedLap && selectedLapRef.current) {
            selectedLapRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            })
        }
    }, [selectedLap, isOpen])

    const processFile = async (file) => {
        setIsProcessing(true)
        setError(null)
        setFileInfo(null)
        setLapsInfo([])
        setSelectedLap(null)
        setTelemetryData(null)
        setPersistedTelemetry(null)
        setProcessingStatus('Reading Alfano file...')
        setProcessingProgress(0)

        try {
            console.log('Processing Alfano file:', file.name, 'Size:', (file.size / 1024).toFixed(2) + ' KB')

            setProcessingStatus('Extracting telemetry data...')

            // Process Alfano ZIP file
            const result = await processAlfanoFile(file, {
                onProgress: ({ stage, progress }) => {
                    setProcessingProgress(progress)

                    if (stage === 'extracting') {
                        setProcessingStatus(`Extracting ZIP file... ${progress}%`)
                    } else if (stage === 'parsing') {
                        setProcessingStatus('Parsing CSV data...')
                    } else if (stage === 'finding-track') {
                        setProcessingStatus('Finding track in database...')
                    } else if (stage === 'detecting-laps') {
                        setProcessingStatus('Detecting laps...')
                    }
                }
            })

            console.log('Processing complete:', {
                totalPoints: result.allPoints.length,
                laps: result.allLaps.length
            })

            setFileInfo({
                name: result.fileName,
                size: (result.fileSize / 1024).toFixed(2) + ' KB',
                totalPoints: result.allPoints.length
            })

            if (Array.isArray(result.allLaps)) {
                setLapsInfo(result.allLaps)
                // Auto-select the first lap if available
                if (result.allLaps.length > 0) {
                    setSelectedLap(result.allLaps[0].lapNumber)
                }
            }

            const defaultLapNumber = result.allLaps[0]?.lapNumber || null
            setProcessingStatus('Saving the original file and building your report...')
            const persisted = await persistAlfanoTelemetry({
                file,
                telemetryData: result,
                selectedLapNumber: defaultLapNumber,
            })

            setTelemetryData(result)
            setPersistedTelemetry(persisted)
            trackEvent("telemetry_import_accepted", {
                product: "alfano",
                placement: "importer",
                stage: "processing",
            })
            setIsOpen(true)
            setUploadModalOpen(false)
            setProcessingStatus('')
        } catch (error) {
            console.error('Error processing Alfano file:', error)
            trackEvent("telemetry_import_error", {
                product: "alfano",
                placement: "importer",
                stage: "processing",
                error_code: "processing_failed",
            })
            setProcessingStatus('')

            let errorMessage = error.message || 'Failed to process Alfano file.'
            let errorDetails = null

            if (error.message?.includes('No CSV or TXT file found')) {
                errorMessage = 'Invalid Alfano File'
                errorDetails = 'The ZIP file doesn\'t contain GPS data.\n\n' +
                    'Please export your Alfano data as a ZIP file that includes CSV or TXT format with GPS coordinates.\n\n' +
                    'Make sure to:\n' +
                    '• Export from Alfano software (Visual Data)\n' +
                    '• Include GPS data in the export\n' +
                    '• Export as ZIP format'
            } else if (error.message?.includes('Could not find GPS coordinates')) {
                errorMessage = 'GPS Data Not Found'
                errorDetails = error.message
            } else if (error.message?.includes('No data found')) {
                errorMessage = 'Empty Data File'
                errorDetails = 'The CSV file appears to be empty or contains no valid data.\n\n' +
                    'Please check that:\n' +
                    '• The file was exported correctly from Alfano software\n' +
                    '• GPS was enabled during your session\n' +
                    '• The session contains lap data'
            }

            setError({ message: errorMessage, details: errorDetails })
        } finally {
            setIsProcessing(false)
        }
    }

    const validateAndProcessFile = (file) => {
        // Accept ZIP files
        if (!file.name.match(/\.zip$/i)) {
            trackEvent("telemetry_import_error", {
                product: "alfano",
                placement: "importer",
                stage: "file_validation",
                error_code: "invalid_file_type",
            })
            setError({
                message: 'Invalid File Type',
                details: 'Please select a valid Alfano ZIP file.\n\n' +
                    'Alfano data should be exported as:\n' +
                    '• .zip file from Visual Data software\n' +
                    '• Contains CSV or TXT with GPS data'
            })
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
            return
        }

        // Check entitlement before processing
        const showGate = () => {
            trackEvent("paddock_pass_view", {
                product: "alfano",
                placement: "importer",
                stage: "entitlement",
                intent: "alfano_import",
            })
            setSubscriptionContext({
                title: 'Paddock Pass required',
                message: 'Paddock Pass is required from your first Alfano import. Subscribe before processing your telemetry file.',
                intent: 'alfano_import'
            })
            setShowSubscriptionWall(true)
            setUploadModalOpen(false)
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        }

        trackEvent("telemetry_import_start", {
            product: "alfano",
            placement: "importer",
            stage: "file_selected",
        })
        requireRacingLineEntitlement({ auth, onAllowed: () => processFile(file), onDenied: showGate })
    }

    const handleFileSelect = (event) => {
        const file = event.target.files[0]
        if (!file) return
        validateAndProcessFile(file)
    }

    const handleDrop = (e) => {
        e.preventDefault()
        setIsDragging(false)

        const file = e.dataTransfer.files[0]
        if (file) {
            validateAndProcessFile(file)
        }
    }

    const handleDragOver = (e) => {
        e.preventDefault()
        setIsDragging(true)
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        setIsDragging(false)
    }

    const openUploadModal = (e) => {
        e.preventDefault()

        requireAuth(() => {
            setUploadModalOpen(true)
            setError(null)
            setProcessingStatus('')
            setProcessingProgress(0)
        })
    }

    const triggerFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click()
        }
    }

    const handleLapSelect = (lapNumber) => {
        setSelectedLap(lapNumber)
    }

    const handleImport = () => {
        if (!telemetryData || selectedLap === null) return

        try {
            const selectedLapData = telemetryData.allLaps.find(lap => lap.lapNumber === selectedLap)
            if (!selectedLapData || !selectedLapData.points) {
                throw new Error('Selected lap data not found')
            }

            validateAlfanoData(selectedLapData.points)

            const pointsWithLapTime = selectedLapData.points.map(point => ({
                ...point,
                lapTime: selectedLapData.formattedTime
            }))

            const importData = {
                carType: 'Alfano Data Logger',
                source: 'alfano',
                vehicleId: 'alfano',
                lapTime: selectedLapData.formattedTime,
                startFinishLat: telemetryData.startFinishLat,
                startFinishLng: telemetryData.startFinishLng
            }

            if (!persistedTelemetry?.id) {
                throw new Error('The Alfano session was not saved. Please try the import again.')
            }

            onImport(pointsWithLapTime, importData, persistedTelemetry.id)
            setIsOpen(false)
        } catch (error) {
            console.error('Error importing lap:', error)
            trackEvent("telemetry_import_error", {
                product: "alfano",
                placement: "importer",
                stage: "selection_validation",
                error_code: "validation_failed",
            })
            onImport(null, error)
        }
    }

    return (
        <>
            <input
                type="file"
                accept=".zip,application/zip"
                onChange={handleFileSelect}
                disabled={isProcessing}
                style={{ display: 'none' }}
                ref={fileInputRef}
            />

            {renderTrigger ? (
                renderTrigger({ onClick: openUploadModal })
            ) : (
                <button
                    onClick={openUploadModal}
                    className="flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-4 bg-white border border-gray-300 text-gray-700 hover:text-gray-900 rounded-xl hover:border-gray-400 hover:shadow-md transition-all duration-200 cursor-pointer"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 sm:w-5 sm:h-5">
                        <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <div className="flex flex-col items-start leading-tight">
                        <span className="text-xs uppercase text-gray-500 font-medium">Import</span>
                        <span className="text-sm sm:text-base font-semibold">Alfano Data</span>
                    </div>
                </button>
            )}

            {showSubscriptionWall && (
              <SubscriptionWall
                onClose={() => setShowSubscriptionWall(false)}
                title={subscriptionContext.title}
                message={subscriptionContext.message}
                intent={subscriptionContext.intent}
              />
            )}

            {/* Upload Modal */}
            {uploadModalOpen && (
                <Dialog
                    open={uploadModalOpen}
                    onClose={() => !isProcessing && setUploadModalOpen(false)}
                    className="fixed inset-0 z-50 overflow-y-auto"
                >
                    <div className="flex min-h-screen items-center justify-center p-3 sm:p-4">
                        <Dialog.Overlay className="fixed inset-0 bg-black/30" />

                    <div className="relative mx-auto max-w-2xl w-full bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 space-y-4 sm:space-y-6">
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                                <Dialog.Title className="text-xl sm:text-2xl font-bold text-gray-900">
                                    Import Alfano Data
                                </Dialog.Title>
                                <p className="mt-1 text-xs sm:text-sm text-gray-600">
                                    Extract GPS racing lines from your Alfano logger
                                </p>
                            </div>
                            {!isProcessing && (
                                <button
                                    onClick={() => setUploadModalOpen(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                                >
                                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>

                        {/* Information Section */}
                        {!isProcessing && !error && (
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                                <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                    What this does:
                                </h3>
                                <ul className="text-sm text-blue-800 space-y-1.5 ml-7">
                                    <li>• Imports GPS telemetry from your Alfano data logger</li>
                                    <li>• Automatically detects laps and track location</li>
                                    <li>• View your racing line in 3D</li>
                                </ul>
                                <div className="mt-3 pt-3 border-t border-blue-200">
                                    <p className="text-xs font-medium text-blue-900 mb-1.5">Requirements:</p>
                                    <ul className="text-xs text-blue-700 space-y-1 ml-3">
                                        <li>• Alfano data logger with GPS (Alfano 6, Alfano 7, etc.)</li>
                                        <li>• Export via share button in Alfano app, or from Visual Data software</li>
                                        <li>• GPS must be enabled during your session</li>
                                        <li>• File format: .zip containing CSV with GPS data</li>
                                    </ul>
                                </div>
                            </div>
                        )}

                        {/* Drag and Drop Area */}
                        {!isProcessing && !error && (
                            <div
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onClick={triggerFileInput}
                                className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
                                    isDragging
                                        ? 'border-indigo-500 bg-indigo-50'
                                        : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
                                }`}
                            >
                                <svg
                                    className={`mx-auto h-16 w-16 ${isDragging ? 'text-indigo-500' : 'text-gray-400'}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={1.5}
                                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                    />
                                </svg>
                                <p className="mt-4 text-lg font-medium text-gray-900">
                                    {isDragging ? 'Drop your Alfano file here' : 'Drag and drop your Alfano ZIP file'}
                                </p>
                                <p className="mt-2 text-sm text-gray-500">
                                    or click to browse your files
                                </p>
                                <p className="mt-2 text-xs text-gray-400">
                                    Supports .zip files from Alfano Visual Data
                                </p>
                            </div>
                        )}

                        {/* Processing State */}
                        {isProcessing && (
                            <div className="py-8 space-y-6">
                                <div className="flex flex-col items-center space-y-4">
                                    <div className="relative">
                                        <svg className="animate-spin h-16 w-16 text-indigo-600" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {processingProgress > 0 && (
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <span className="text-sm font-bold text-indigo-600">{processingProgress}%</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-center">
                                        <p className="text-lg font-medium text-gray-900">{processingStatus}</p>
                                        <p className="mt-1 text-sm text-gray-500">This may take a moment...</p>
                                    </div>
                                </div>
                                {processingProgress > 0 && (
                                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                        <div
                                            className="bg-indigo-600 h-full transition-all duration-300 ease-out"
                                            style={{ width: `${processingProgress}%` }}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Error State */}
                        {error && !isProcessing && (
                            <div className="py-6 space-y-6">
                                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 space-y-4">
                                    <div className="flex items-start gap-3">
                                        <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                        <div className="flex-1">
                                            <h3 className="font-semibold text-red-900 text-lg">{error.message}</h3>
                                            {error.details && (
                                                <div className="mt-3 text-sm text-red-800 whitespace-pre-line">
                                                    {error.details}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-center">
                                    <button
                                        onClick={() => {
                                            setError(null)
                                            setProcessingProgress(0)
                                        }}
                                        className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
                                    >
                                        Try Another File
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Dialog>
            )}

            {/* Lap Selection Modal */}
            {isOpen && (
            <Dialog
                open={isOpen}
                onClose={() => setIsOpen(false)}
                className="fixed inset-0 z-50 overflow-y-auto"
            >
                <div className="flex min-h-screen items-center justify-center">
                    <Dialog.Overlay className="fixed inset-0 bg-black/30" />

                    <div className="relative mx-auto max-w-2xl w-full bg-white rounded-2xl shadow-lg p-6 space-y-6">
                        <Dialog.Title className="text-2xl font-bold text-gray-900">
                            Import Alfano Lap
                        </Dialog.Title>

                        {fileInfo && (
                            <div className="space-y-2">
                                <h3 className="text-lg font-semibold text-gray-900">File Information</h3>
                                <div className="bg-gray-50 p-4 rounded-xl space-y-2">
                                    <p className="text-gray-700">File: {fileInfo.name}</p>
                                    <p className="text-gray-700">Size: {fileInfo.size}</p>
                                    <p className="text-gray-700">GPS Points: {fileInfo.totalPoints.toLocaleString()}</p>
                                </div>
                            </div>
                        )}

                        {lapsInfo.length > 0 && (
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-gray-900">Select a Lap</h3>
                                <p className="text-sm text-gray-600">
                                    {lapsInfo.length === 1
                                        ? 'No complete laps detected. Showing entire recording.'
                                        : `${lapsInfo.length} laps detected. Select the lap you want to import.`}
                                </p>
                                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                                    {lapsInfo.map((lap) => (
                                        <div
                                            key={lap.lapNumber}
                                            ref={selectedLap === lap.lapNumber ? selectedLapRef : null}
                                            onClick={() => handleLapSelect(lap.lapNumber)}
                                            className={`p-4 rounded-xl cursor-pointer transition-all
                                                ${selectedLap === lap.lapNumber
                                                    ? 'bg-indigo-50 border-2 border-indigo-300 shadow-sm'
                                                    : 'bg-gray-50 hover:bg-gray-100'}`}
                                        >
                                            <div className="flex justify-between items-center">
                                                <span className="font-medium text-gray-900">
                                                    {lapsInfo.length === 1 ? 'Full Recording' : `Lap ${lap.lapNumber}`}
                                                </span>
                                                <span className="font-mono text-gray-700">
                                                    {lap.formattedTime}
                                                </span>
                                                <span className="text-sm text-gray-500">
                                                    {lap.points.length} points
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex justify-end space-x-4 pt-4 border-t">
                                    <button
                                        onClick={() => setIsOpen(false)}
                                        className="px-4 py-2 rounded-lg font-medium text-gray-700 hover:bg-gray-100"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleImport}
                                        disabled={selectedLap === null}
                                        className={`px-6 py-2 rounded-lg font-medium transition-colors
                                            ${selectedLap === null
                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
                                    >
                                        Import Selected Lap
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Dialog>
            )}
        </>
    )
}

AlfanoImport.propTypes = {
    onImport: PropTypes.func.isRequired,
    renderTrigger: PropTypes.func,
    triggerOpen: PropTypes.bool
}

export default AlfanoImport
